import React from 'react';
import { 
  Tooltip, 
  Box, 
  Typography, 
  List, 
  ListItem, 
  ListItemText,
  ListItemIcon
} from '@mui/material';
import { styled } from '@mui/material/styles';

// Custom styled tooltip
const StyledTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .MuiTooltip-tooltip`]: {
    backgroundColor: 'white',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 800,
    border: '1px solid #dadde9',
    padding: 0,
    boxShadow: theme.shadows[1],
  },
}));

// Company tooltip data
const companyData = {
  'CEVA Logistics': {
    employees: '110,000+',
    countries: 'Present in 160+',
    collaborationStart: '2021',
    keyAchievements: [
      'Automation of repetitive tasks using UiPath, VBA, and Python',
      'Automated large volume of sanity checks, even outside business hours',
      'Workload Reduction: 63%'
    ],
    automatedProcesses: [
      'Sanity checks',
      'Network flow management',
      'Reporting',
      'Ticket management'
    ]
  },
  'La Poste': {
    employees: '238,000+',
    parcelsDelivered: 'Around 15 billion in 2021',
    collaborationStart: '2021',
    keyAchievements: [
      'Automation of repetitive tasks using UiPath, VBA, Python, and PowerPlatform',
      'Developed a custom application to automate the ticketing process',
      'Automated the resolving of alarms from a monitoring tool',
      'Workload Reduction: 40%'
    ],
    automatedProcesses: [
      'Sanity checks',
      'Email automation',
      'Reporting',
      'Ticket management', 
      'Custom application development'
    ]
  },
  'Europcar Mobility Group': {
    employees: '12,000+',
    countries: 'Present in 135+',
    collaborationStart: '2021',
    keyAchievements: [
      'Automation of repetitive tasks using UiPath, VBA, and Python',
      'Automated high volume of sanity checks and addressing blocked fields preventing rental validation',
      'Workload Reduction: 50%'
    ],
    automatedProcesses: [
      'Sanity checks',
      'Network flow management',
      'Reporting',
      'Ticket management',
      'Security controls',
      'Automatic remediation'
    ]
  }
};

const CompanyTooltip = ({ company, children }) => {
  const data = companyData[company];
  
  if (!data) {
    return children;
  }
  
  return (
    <StyledTooltip
      title={
        <Box sx={{ p: 2, width: '800px', boxSizing: 'border-box' }}>
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 'bold', 
              mb: 1.5, 
              color: '#db1d2b',
              borderBottom: '2px solid',
              borderColor: '#db1d2b',
              pb: 1,
              width: '100%',
              boxSizing: 'border-box'
            }}
          >
            {company}
          </Typography>
          
          <Box sx={{ mb: 3 }}>
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#666',
                mb: 0.5,
                lineHeight: 1.6,
                width: '100%',
                boxSizing: 'border-box'
              }}
            >
              <strong>Employees:</strong> {data.employees}
            </Typography>
            
            {data.countries && (
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#666',
                  mb: 0.5,
                  lineHeight: 1.6,
                  width: '100%',
                  boxSizing: 'border-box'
                }}
              >
                <strong>Countries:</strong> {data.countries}
              </Typography>
            )}
            
            {data.parcelsDelivered && (
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#666',
                  mb: 0.5,
                  lineHeight: 1.6,
                  width: '100%',
                  boxSizing: 'border-box'
                }}
              >
                <strong>Parcels Delivered:</strong> {data.parcelsDelivered}
              </Typography>
            )}
            
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#666',
                mb: 0.5,
                lineHeight: 1.6,
                width: '100%',
                boxSizing: 'border-box'
              }}
            >
              <strong>Collaboration Start:</strong> {data.collaborationStart}
            </Typography>
          </Box>
          
          <Typography 
            variant="subtitle1" 
            sx={{ 
              fontWeight: 'bold', 
              color: '#f47216',
              mb: 1.5,
              width: '100%',
              boxSizing: 'border-box'
            }}
          >
            Key Achievements:
          </Typography>
          <List dense sx={{ p: 0, width: '100%', boxSizing: 'border-box' }}>
            {data.keyAchievements.map((achievement, index) => (
              <ListItem 
                key={index} 
                sx={{ 
                  py: 0.5,
                  color: '#444',
                  width: '100%',
                  boxSizing: 'border-box',
                  '&:hover': {
                    bgcolor: 'rgba(219, 29, 43, 0.04)',
                    borderRadius: '4px'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: '30px' }}>
                  <span role="img" aria-label="achievement">🎯</span>
                </ListItemIcon>
                <ListItemText 
                  primary={achievement} 
                  primaryTypographyProps={{
                    sx: { 
                      fontSize: '0.9rem',
                      width: '100%',
                      boxSizing: 'border-box'
                    }
                  }}
                />
              </ListItem>
            ))}
          </List>
          
          <Typography 
            variant="subtitle1" 
            sx={{ 
              fontWeight: 'bold', 
              color: '#f47216',
              mb: 1.5,
              mt: 2,
              width: '100%',
              boxSizing: 'border-box'
            }}
          >
            Automated Processes:
          </Typography>
          <List dense sx={{ p: 0, width: '100%', boxSizing: 'border-box', display: 'flex', flexWrap: 'wrap' }}>
            {data.automatedProcesses.map((process, index) => (
              <ListItem 
                key={index} 
                sx={{ 
                  py: 0.5,
                  color: '#444',
                  width: '50%',
                  boxSizing: 'border-box',
                  '&:hover': {
                    bgcolor: 'rgba(219, 29, 43, 0.04)',
                    borderRadius: '4px'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: '30px' }}>
                  <span role="img" aria-label="process">⚙️</span>
                </ListItemIcon>
                <ListItemText 
                  primary={process} 
                  primaryTypographyProps={{
                    sx: { 
                      fontSize: '0.9rem',
                      width: '100%',
                      boxSizing: 'border-box'
                    }
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      }
      arrow
      placement="top"
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: 'white',
            '& .MuiTooltip-arrow': {
              color: 'white',
            },
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            borderRadius: '8px',
            p: 0,
            width: '800px',
            maxWidth: 'none'
          },
        },
      }}
    >
      {children}
    </StyledTooltip>
  );
};

export default CompanyTooltip; 